import * as XLSX from "xlsx";
import {WorkSheet} from "xlsx";
import {CMEDirectTrade, Trade, ICEBlockTrade, WebICETrade} from "../../types/APITypes";
import {DateTime} from "luxon";
import {formatCmeTrade} from "./conf/cmeColumns";
import {formatICEBlockTrade} from "./conf/iceBlockColumns";
import {formatWebICETrade} from "./conf/webICEColumns";
import {formatVocsetTrade} from "./conf/vocsetColumns";

export const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
    arr.reduce((groups, item) => {
        (groups[key(item)] ||= []).push(item);
        return groups;
    }, {} as Record<K, T[]>);

export const processTradeFile  =async function processTradeFile(_fileName: string | undefined, fileArrayBuffer: ArrayBuffer): Promise<Trade[]> {
    let _wbDdata;
    if (_fileName?.toUpperCase().endsWith('CSV')) {
        console.log('CSV File: ', _fileName);
        _wbDdata = XLSX.read(fileArrayBuffer, {
            type: "binary",
            cellDates: true,
            UTC: true,
            dense: true
        });
    } else {
        console.log('XLS File: ', _fileName);
        _wbDdata = XLSX.read(fileArrayBuffer, {
            cellText: false,
            cellDates: true,
            dense: true
        });
    }

    const _wsData = _wbDdata.Sheets[_wbDdata.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(_wsData, {
        header: 1,
        raw: true,
        UTC: true,
        //dateNF: 'yyyy-mm-dd',
        defval: null,
        skipHidden: true,
        blankrows: false
    });
    if (jsonData.length > 0) {
        return readTradeObjects(jsonData);
    } else {
        return [];
    }
};


function arrayToObjects(arr: any[][]): any[] {
    let objArr: any[] = [];
    let headers: string[] = arr[0];
    for (const r of arr.slice(1)) {
        let obj = {};
        for (let i=0; i < headers.length; i++) {
            // @ts-ignore
            obj[headers[i]] = r[i];
        }
        objArr.push(obj);
    }
    return objArr;
}

async function readTradeObjects(data: any[]): Promise<Trade[]> {
    const webICEFormatDetect = data.findIndex(row => typeof row[0] === 'string' && row[0] === 'Futures Deals');
    if (webICEFormatDetect >= 0) {
        let trades = arrayToObjects(data.slice(webICEFormatDetect+1)) as WebICETrade[];     // skip forward to header row
        console.log('WebICE Formatted Trades - from row: ', webICEFormatDetect+1, trades);
        return Promise.all(trades.map(formatWebICETrade));
    }

    const iceIDIndex = data.findIndex(row => typeof row[0] === 'string' && row[0] === 'ICE ID');
    if (iceIDIndex >= 0) {
        let trades = arrayToObjects(data.slice(iceIDIndex)) as ICEBlockTrade[];
        // Remove the strategy trades - by using Option = uds
        trades = trades.filter(trade => trade['Option'] !== 'uds');
        console.log('ICEBlock Formatted Trades - from row: ', iceIDIndex);
        return Promise.all(trades.map(formatICEBlockTrade));
    }

    let header: string[] = data[0];
    const cmeIndex = header.findIndex(cell => cell === 'Source');
    const cmeCheck = cmeIndex >= 0 && data.length > 2 && data[1][cmeIndex] == 'CMED';
    if (cmeCheck) {
        let trades = arrayToObjects(data) as CMEDirectTrade[];
        console.log('CME Formatted Trades');
        // Remove the strategy trades - by using Cleared Contracts = 0
        trades = trades.filter(trade => trade['Cleared Contracts']);
        return Promise.all(trades.map(formatCmeTrade));
    }

    let trades = arrayToObjects(data) as Trade[];
    console.log('VOCSET Formatted Trades');
    return Promise.all(trades.map(formatVocsetTrade));
}

export function fromJSDateToDateTime(inDate: Date): DateTime | undefined {
    if (inDate) {
        const _dateTime = DateTime.fromJSDate(inDate).setZone('UTC');
        if (!_dateTime.isValid) {
            console.log('DateTime :', _dateTime.invalidExplanation);
        }
        return _dateTime;
    }
    return undefined;
}

export function convertToDateTime(inDate: any): DateTime | undefined {
    if (typeof inDate === 'object') {
        return fromJSDateToDateTime(inDate);
    } else {
        return DateTime.fromISO(inDate as string).setZone('utc');
    }
}

export function mergeDateTime(dateOnly: DateTime, timeOnly: DateTime): DateTime {
    return timeOnly.set({ day: dateOnly.day, month: dateOnly.month, year: dateOnly.year });
}
